import Home from '../containers/Home/Home.jsx';
import JuryShell from '../containers/JuryVoting/JuryShell/JuryShell.jsx';
import Project from '../containers/Project/Project.jsx';
import ProjectList from '../containers/ProjectList/ProjectList.jsx';
import SignUp from '../containers/SignUp/SignUp.jsx';
import SMSRequest from '../containers/SMSRequest/SMSRequest.jsx';

/**
 * Art eines Eintrags in der gemeinsamen Routentabelle. Die Tabelle selbst ist reine
 * Daten, damit die Apps sie mit ihren eigenen Bausteinen fuellen koennen, ohne dass
 * hier eine Produktweiche steht.
 */
export const ROUTE_KIND = {
    /** `/__live_preview` – rendert die geteilte `LivePreviewPage`. */
    LIVE_PREVIEW: 'livePreview',
    /** Die aus dem CMS geladenen Seiten (`/pages/`). */
    DYNAMIC_PAGES: 'dynamicPages',
    /** Route mit einer Komponente, die im Paket liegt. */
    COMPONENT: 'component',
    /** Route, deren Komponente ein Container-Slot ist (siehe `CONTAINER_SLOTS`). */
    CONTAINER_SLOT: 'containerSlot',
    /** Einfuegepunkt fuer die produktspezifischen Zusatzrouten (`extraRoutes`). */
    EXTRA: 'extra',
    /** Der 404-Fall. */
    NOT_FOUND: 'notFound',
};

/**
 * Container-Slots der Routentabelle. Jeder Slot hat einen Kern-Container; eine App kann
 * ihn ueber `containers` ersetzen (Kampagnen-Zweige tun das), muss es aber nicht mehr.
 */
export const CONTAINER_SLOTS = ['Project', 'ProjectList', 'SignUp'];

/**
 * Kern-Container je Slot. Sie greifen, sobald die App keinen eigenen Container liefert;
 * die Produktunterschiede stecken in ihrem Deskriptor (`product.js`), nicht in Kopien.
 */
export const DEFAULT_CONTAINERS = { Project, ProjectList, SignUp };

/**
 * Die gemeinsame Routentabelle in der Reihenfolge, in der `<Switch>` sie auswertet.
 *
 * `injectRouteProps: false` bildet die Stelle nach, an der die Apps ihre Projektliste
 * ohne Route-Props gemountet haben; alle anderen Routen bekommen `match`, `location`
 * und `history` durchgereicht.
 */
export const CORE_ROUTE_TABLE = [
    { kind: ROUTE_KIND.LIVE_PREVIEW, path: '/__live_preview' },
    { kind: ROUTE_KIND.DYNAMIC_PAGES },
    { kind: ROUTE_KIND.CONTAINER_SLOT, slot: 'Project', path: '/projekte/:id' },
    { kind: ROUTE_KIND.COMPONENT, path: '/code-anfordern', component: SMSRequest },
    { kind: ROUTE_KIND.COMPONENT, path: '/jury', component: JuryShell, juryCopy: true, juryVotingModel: true },
    { kind: ROUTE_KIND.CONTAINER_SLOT, slot: 'ProjectList', path: '/projekte', injectRouteProps: false },
    { kind: ROUTE_KIND.EXTRA },
    { kind: ROUTE_KIND.CONTAINER_SLOT, slot: 'SignUp', path: '/bewerbung' },
    { kind: ROUTE_KIND.COMPONENT, path: '/', component: Home, exact: true },
    { kind: ROUTE_KIND.NOT_FOUND, path: '*' },
];

/**
 * Loest die Routentabelle mit den Bausteinen der aufrufenden App auf.
 *
 * @param {object} [options]
 * @param {Record<string, Function|object>} [options.containers] Overrides je Slot-Name aus
 *        {@link CONTAINER_SLOTS}. Fehlt ein Slot, greift der Kern-Container aus
 *        {@link DEFAULT_CONTAINERS}; unbekannte Namen werden gemeldet und ignoriert.
 * @param {Array<{path: string, component: Function|object, exact?: boolean}>} [options.extraRoutes]
 *        Produktspezifische Zusatzrouten; sie landen zwischen `/projekte` und `/bewerbung`.
 * @returns {Array<object>} Aufgeloeste Routeneintraege in Switch-Reihenfolge.
 */
export const buildRouteTable = ({ containers = {}, extraRoutes = [] } = {}) => {
    const unknownSlots = Object.keys(containers).filter((slot) => !CONTAINER_SLOTS.includes(slot));

    if (unknownSlots.length > 0) {
        // Ein Tippfehler im Slot-Namen fiele sonst still auf den Kern-Container zurueck.
        console.warn(
            `[routes] Unbekannte Container-Slots: ${unknownSlots.join(', ')}. Bekannt: ${CONTAINER_SLOTS.join(', ')}.`
        );
    }

    const resolvedContainers = CONTAINER_SLOTS.reduce((resolved, slot) => {
        resolved[slot] = containers[slot] ?? DEFAULT_CONTAINERS[slot];
        return resolved;
    }, {});

    return CORE_ROUTE_TABLE.flatMap((entry) => {
        if (entry.kind === ROUTE_KIND.CONTAINER_SLOT) {
            return [{ ...entry, component: resolvedContainers[entry.slot] }];
        }

        if (entry.kind === ROUTE_KIND.EXTRA) {
            return extraRoutes.map((route) => ({ kind: ROUTE_KIND.COMPONENT, ...route }));
        }

        return [{ ...entry }];
    });
};
